/**
 * Quiz Service
 *
 * Manages weekly tech quizzes including:
 * - Curated weekly question sets (with fallback random generation)
 * - Question bank with UniFi, networking, WiFi, Starlink, security, company topics
 * - Weekly quiz generation (resets Monday 6am NSW)
 * - Quiz attempts and scoring
 * - Stats aggregation (streaks count consecutive weeks)
 */

import { db } from '@/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  WeeklyQuiz,
  QuizQuestion,
  QuizAttempt,
  TechQuizStats,
  QuizCategory,
} from '@/types';
import { differenceInDays, startOfISOWeek, getISOWeek, getISOWeekYear } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { getTimezoneSync } from '@/utils/timezone';

const QUIZ_RESET_HOUR = 6; // 6am local time — quiz week rolls over Monday 6am

// ============================================================================
// QUIZ PAUSE FLAG
// ============================================================================
// The weekly quiz is paused while the question bank + UX get an overhaul.
// Flip QUIZ_PAUSED back to false (or remove the gate) once the new quiz ships.
export const QUIZ_PAUSED = true;
export const QUIZ_RESUME_LABEL = 'July';
export const QUIZ_PAUSE_HEADLINE = 'Quiz is getting an overhaul';
export const QUIZ_PAUSE_MESSAGE = `The weekly quiz is on pause while we build something better. It'll be back in ${QUIZ_RESUME_LABEL}.`;

// ============================================================================
// QUESTION BANK - Comprehensive tech questions
// ============================================================================

const QUESTION_BANK: QuizQuestion[] = [
  // ==================== UNIFI QUESTIONS ====================
  {
    id: 'unifi-001',
    question: 'What is the default IP address for a UniFi Dream Machine Pro?',
    options: {
      a: '192.168.1.1',
      b: '10.0.0.1',
      c: '192.168.0.1',
    },
    correctAnswer: 'a',
    explanation:
      'UniFi Dream Machine Pro uses 192.168.1.1 as its default gateway IP address.',
    category: 'unifi',
    difficulty: 'easy',
  },
  {
    id: 'unifi-002',
    question: 'Which UniFi feature allows you to create separate networks for guests?',
    options: {
      a: 'VLAN Tagging',
      b: 'Guest Network Portal',
      c: 'Traffic Management',
    },
    correctAnswer: 'b',
    explanation:
      'Guest Network Portal in UniFi allows creating isolated guest networks with captive portals.',
    category: 'unifi',
    difficulty: 'easy',
  },
  {
    id: 'unifi-003',
    question: 'What does "Meshing" mean in UniFi access point terminology?',
    options: {
      a: 'Connecting APs via ethernet cables',
      b: 'APs connecting wirelessly to extend coverage',
      c: 'Creating multiple SSIDs',
    },
    correctAnswer: 'b',
    explanation:
      'Meshing allows UniFi APs to connect wirelessly to the network, extending coverage without ethernet.',
    category: 'unifi',
    difficulty: 'medium',
  },
  {
    id: 'unifi-004',
    question: 'What is the purpose of UniFi Protect?',
    options: {
      a: 'Network firewall protection',
      b: 'Video surveillance and camera management',
      c: 'Access point security',
    },
    correctAnswer: 'b',
    explanation:
      'UniFi Protect is Ubiquiti\'s video surveillance platform for managing cameras and NVRs.',
    category: 'unifi',
    difficulty: 'easy',
  },
  {
    id: 'unifi-005',
    question: 'Which LED color on a UniFi AP indicates it is isolated (not adopted)?',
    options: {
      a: 'Solid blue',
      b: 'Solid white',
      c: 'Flashing white',
    },
    correctAnswer: 'c',
    explanation:
      'A flashing white LED indicates the AP is factory default and waiting to be adopted.',
    category: 'unifi',
    difficulty: 'medium',
  },
  {
    id: 'unifi-006',
    question: 'What is the maximum number of devices a UniFi U6 Pro can handle?',
    options: {
      a: '100 devices',
      b: '300 devices',
      c: '500 devices',
    },
    correctAnswer: 'b',
    explanation:
      'The UniFi U6 Pro supports up to 300 concurrent client devices.',
    category: 'unifi',
    difficulty: 'hard',
  },
  {
    id: 'unifi-007',
    question: 'What does "Adopt" mean in UniFi terminology?',
    options: {
      a: 'Updating firmware',
      b: 'Adding a device to the controller',
      c: 'Resetting to factory defaults',
    },
    correctAnswer: 'b',
    explanation:
      'Adopting a device connects it to your UniFi Controller for management.',
    category: 'unifi',
    difficulty: 'easy',
  },
  {
    id: 'unifi-008',
    question: 'Which UniFi product combines router, switch, and access point in one device?',
    options: {
      a: 'UniFi Switch Pro',
      b: 'UniFi Dream Machine',
      c: 'UniFi Security Gateway',
    },
    correctAnswer: 'b',
    explanation:
      'The UniFi Dream Machine is an all-in-one device with router, switch, AP, and controller.',
    category: 'unifi',
    difficulty: 'easy',
  },

  // ==================== NETWORKING QUESTIONS ====================
  {
    id: 'net-001',
    question: 'What is the maximum cable length for Cat6 ethernet?',
    options: {
      a: '50 meters',
      b: '100 meters',
      c: '150 meters',
    },
    correctAnswer: 'b',
    explanation:
      'Cat6 cable supports up to 100 meters (328 feet) for 1Gbps speeds.',
    category: 'networking',
    difficulty: 'easy',
  },
  {
    id: 'net-002',
    question: 'What does DHCP stand for?',
    options: {
      a: 'Dynamic Host Configuration Protocol',
      b: 'Domain Host Control Protocol',
      c: 'Direct Host Connection Protocol',
    },
    correctAnswer: 'a',
    explanation:
      'DHCP automatically assigns IP addresses to devices on a network.',
    category: 'networking',
    difficulty: 'easy',
  },
  {
    id: 'net-003',
    question: 'Which port does HTTPS use by default?',
    options: {
      a: '80',
      b: '443',
      c: '8080',
    },
    correctAnswer: 'b',
    explanation: 'HTTPS (HTTP Secure) uses port 443 by default.',
    category: 'networking',
    difficulty: 'easy',
  },
  {
    id: 'net-004',
    question: 'What is a VLAN used for?',
    options: {
      a: 'Increasing network speed',
      b: 'Segmenting network traffic',
      c: 'Connecting to the internet',
    },
    correctAnswer: 'b',
    explanation:
      'VLANs (Virtual LANs) segment network traffic for security and organization.',
    category: 'networking',
    difficulty: 'medium',
  },
  {
    id: 'net-005',
    question: 'What does NAT stand for?',
    options: {
      a: 'Network Access Terminal',
      b: 'Network Address Translation',
      c: 'Node Assignment Table',
    },
    correctAnswer: 'b',
    explanation:
      'NAT translates private IP addresses to public IPs for internet access.',
    category: 'networking',
    difficulty: 'medium',
  },
  {
    id: 'net-006',
    question: 'What is the subnet mask for a /24 network?',
    options: {
      a: '255.255.0.0',
      b: '255.255.255.0',
      c: '255.255.255.128',
    },
    correctAnswer: 'b',
    explanation: 'A /24 network has a subnet mask of 255.255.255.0 (256 addresses).',
    category: 'networking',
    difficulty: 'medium',
  },
  {
    id: 'net-007',
    question: 'Which layer of the OSI model do switches operate at?',
    options: {
      a: 'Layer 1 (Physical)',
      b: 'Layer 2 (Data Link)',
      c: 'Layer 3 (Network)',
    },
    correctAnswer: 'b',
    explanation:
      'Layer 2 switches operate at the Data Link layer using MAC addresses.',
    category: 'networking',
    difficulty: 'medium',
  },
  {
    id: 'net-008',
    question: 'What does PoE stand for?',
    options: {
      a: 'Power over Ethernet',
      b: 'Protocol over Ethernet',
      c: 'Port over Ethernet',
    },
    correctAnswer: 'a',
    explanation:
      'PoE delivers electrical power along with data over ethernet cables.',
    category: 'networking',
    difficulty: 'easy',
  },
  {
    id: 'net-009',
    question: 'What is the purpose of a DNS server?',
    options: {
      a: 'Assign IP addresses',
      b: 'Translate domain names to IP addresses',
      c: 'Filter network traffic',
    },
    correctAnswer: 'b',
    explanation:
      'DNS (Domain Name System) translates human-readable domains to IP addresses.',
    category: 'networking',
    difficulty: 'easy',
  },
  {
    id: 'net-010',
    question: 'What is the difference between a hub and a switch?',
    options: {
      a: 'Hubs are faster than switches',
      b: 'Switches send data only to the intended recipient',
      c: 'Hubs support PoE, switches do not',
    },
    correctAnswer: 'b',
    explanation:
      'Switches intelligently forward traffic to specific ports; hubs broadcast to all ports.',
    category: 'networking',
    difficulty: 'easy',
  },

  // ==================== WIFI QUESTIONS ====================
  {
    id: 'wifi-001',
    question: 'What frequency does WiFi 6 (802.11ax) operate on?',
    options: {
      a: '2.4 GHz only',
      b: '5 GHz only',
      c: 'Both 2.4 GHz and 5 GHz',
    },
    correctAnswer: 'c',
    explanation: 'WiFi 6 operates on both 2.4 GHz and 5 GHz frequency bands.',
    category: 'wifi',
    difficulty: 'easy',
  },
  {
    id: 'wifi-002',
    question: 'Which WiFi band typically provides better range but lower speed?',
    options: {
      a: '2.4 GHz',
      b: '5 GHz',
      c: '6 GHz',
    },
    correctAnswer: 'a',
    explanation:
      '2.4 GHz has longer wavelengths, providing better range but more interference.',
    category: 'wifi',
    difficulty: 'easy',
  },
  {
    id: 'wifi-003',
    question: 'What does SSID stand for?',
    options: {
      a: 'Secure Service Internet Domain',
      b: 'Service Set Identifier',
      c: 'System Signal ID',
    },
    correctAnswer: 'b',
    explanation: 'SSID (Service Set Identifier) is the name of a WiFi network.',
    category: 'wifi',
    difficulty: 'easy',
  },
  {
    id: 'wifi-004',
    question: 'What is channel width in WiFi?',
    options: {
      a: 'The physical size of the antenna',
      b: 'The frequency range used for transmission',
      c: 'The number of connected devices',
    },
    correctAnswer: 'b',
    explanation:
      'Channel width (20/40/80/160 MHz) determines how much frequency spectrum is used.',
    category: 'wifi',
    difficulty: 'medium',
  },
  {
    id: 'wifi-005',
    question: 'What is the recommended channel for 2.4 GHz to avoid overlap?',
    options: {
      a: '1, 5, or 9',
      b: '1, 6, or 11',
      c: '2, 6, or 10',
    },
    correctAnswer: 'b',
    explanation:
      'Channels 1, 6, and 11 are non-overlapping in the 2.4 GHz band.',
    category: 'wifi',
    difficulty: 'medium',
  },
  {
    id: 'wifi-006',
    question: 'What does MU-MIMO stand for?',
    options: {
      a: 'Multi-User Multiple Input Multiple Output',
      b: 'Maximum User MIMO',
      c: 'Managed Unified MIMO',
    },
    correctAnswer: 'a',
    explanation:
      'MU-MIMO allows simultaneous data transmission to multiple devices.',
    category: 'wifi',
    difficulty: 'medium',
  },
  {
    id: 'wifi-007',
    question: 'What is the main benefit of WiFi 6E?',
    options: {
      a: 'Longer range',
      b: 'Access to 6 GHz band',
      c: 'Lower power consumption',
    },
    correctAnswer: 'b',
    explanation:
      'WiFi 6E extends into the 6 GHz band, providing more channels and less congestion.',
    category: 'wifi',
    difficulty: 'medium',
  },
  {
    id: 'wifi-008',
    question: 'What encryption standard should you use for maximum WiFi security?',
    options: {
      a: 'WEP',
      b: 'WPA2',
      c: 'WPA3',
    },
    correctAnswer: 'c',
    explanation:
      'WPA3 is the latest and most secure WiFi encryption standard.',
    category: 'wifi',
    difficulty: 'easy',
  },

  // ==================== STARLINK QUESTIONS ====================
  {
    id: 'starlink-001',
    question: 'What type of orbit do Starlink satellites use?',
    options: {
      a: 'Geostationary orbit',
      b: 'Low Earth orbit (LEO)',
      c: 'Medium Earth orbit',
    },
    correctAnswer: 'b',
    explanation:
      'Starlink uses Low Earth Orbit (~550km) for lower latency than traditional satellites.',
    category: 'starlink',
    difficulty: 'easy',
  },
  {
    id: 'starlink-002',
    question: 'What is the typical latency of Starlink internet?',
    options: {
      a: '20-40 ms',
      b: '100-150 ms',
      c: '500-700 ms',
    },
    correctAnswer: 'a',
    explanation:
      'Starlink achieves 20-40ms latency due to low Earth orbit positioning.',
    category: 'starlink',
    difficulty: 'medium',
  },
  {
    id: 'starlink-003',
    question:
      'What technology does the Starlink dish use to track satellites?',
    options: {
      a: 'Manual alignment',
      b: 'Phased array antenna',
      c: 'Parabolic dish tracking',
    },
    correctAnswer: 'b',
    explanation:
      'Starlink uses a phased array antenna that electronically steers to track satellites.',
    category: 'starlink',
    difficulty: 'medium',
  },
  {
    id: 'starlink-004',
    question: 'What is "Obstruction" in the Starlink app?',
    options: {
      a: 'Network congestion',
      b: 'Objects blocking the dish\'s view of the sky',
      c: 'Hardware malfunction',
    },
    correctAnswer: 'b',
    explanation:
      'Obstructions are trees, buildings, or objects blocking the dish\'s sky view.',
    category: 'starlink',
    difficulty: 'easy',
  },
  {
    id: 'starlink-005',
    question: 'What is the recommended clear sky view for Starlink?',
    options: {
      a: '50° from horizontal',
      b: '100° field of view',
      c: '360° unobstructed',
    },
    correctAnswer: 'b',
    explanation:
      'Starlink needs approximately 100° of clear sky view for optimal performance.',
    category: 'starlink',
    difficulty: 'medium',
  },
  {
    id: 'starlink-006',
    question: 'How does the Starlink dish heat itself in snow conditions?',
    options: {
      a: 'It doesn\'t - manual snow removal required',
      b: 'Built-in heating element',
      c: 'Solar panels',
    },
    correctAnswer: 'b',
    explanation:
      'The Starlink dish has a built-in heating system to melt snow and ice.',
    category: 'starlink',
    difficulty: 'easy',
  },
  {
    id: 'starlink-007',
    question: 'What is "Dishy" in Starlink terminology?',
    options: {
      a: 'The mobile app',
      b: 'The user terminal/dish',
      c: 'The router',
    },
    correctAnswer: 'b',
    explanation:
      '"Dishy" or "Dishy McFlatface" is the nickname for the Starlink user terminal.',
    category: 'starlink',
    difficulty: 'easy',
  },
  {
    id: 'starlink-008',
    question: 'What power does the standard Starlink dish consume?',
    options: {
      a: '20-50W',
      b: '75-100W',
      c: '150-200W',
    },
    correctAnswer: 'b',
    explanation:
      'The standard Starlink dish consumes approximately 75-100W during operation.',
    category: 'starlink',
    difficulty: 'hard',
  },

  // ==================== SECURITY QUESTIONS ====================
  {
    id: 'sec-001',
    question: 'What is the purpose of a firewall?',
    options: {
      a: 'Speed up internet connection',
      b: 'Monitor and control network traffic',
      c: 'Store backup data',
    },
    correctAnswer: 'b',
    explanation:
      'Firewalls monitor and control incoming/outgoing network traffic based on rules.',
    category: 'security',
    difficulty: 'easy',
  },
  {
    id: 'sec-002',
    question: 'What is two-factor authentication (2FA)?',
    options: {
      a: 'Using two passwords',
      b: 'Using something you know and something you have',
      c: 'Logging in from two devices',
    },
    correctAnswer: 'b',
    explanation:
      '2FA requires two different types of verification (password + code/device).',
    category: 'security',
    difficulty: 'easy',
  },
  {
    id: 'sec-003',
    question: 'What is a VPN used for?',
    options: {
      a: 'Speeding up internet',
      b: 'Creating an encrypted tunnel for data',
      c: 'Blocking advertisements',
    },
    correctAnswer: 'b',
    explanation:
      'A VPN creates an encrypted connection to protect data and privacy.',
    category: 'security',
    difficulty: 'easy',
  },
  {
    id: 'sec-004',
    question: 'What does IDS stand for in network security?',
    options: {
      a: 'Internet Defense System',
      b: 'Intrusion Detection System',
      c: 'Internal Data Security',
    },
    correctAnswer: 'b',
    explanation:
      'IDS monitors network traffic for suspicious activity and alerts administrators.',
    category: 'security',
    difficulty: 'medium',
  },
  {
    id: 'sec-005',
    question: 'What is the difference between IDS and IPS?',
    options: {
      a: 'IDS is hardware, IPS is software',
      b: 'IDS detects threats, IPS blocks them',
      c: 'They are the same thing',
    },
    correctAnswer: 'b',
    explanation:
      'IDS (Detection) alerts about threats; IPS (Prevention) actively blocks them.',
    category: 'security',
    difficulty: 'medium',
  },
  {
    id: 'sec-006',
    question: 'What is a MAC address used for in security?',
    options: {
      a: 'Encryption',
      b: 'Device identification on a network',
      c: 'User authentication',
    },
    correctAnswer: 'b',
    explanation:
      'MAC addresses uniquely identify network devices and can be used for filtering.',
    category: 'security',
    difficulty: 'easy',
  },
  {
    id: 'sec-007',
    question: 'What type of attack floods a network with traffic?',
    options: {
      a: 'Phishing',
      b: 'DDoS (Distributed Denial of Service)',
      c: 'Man-in-the-middle',
    },
    correctAnswer: 'b',
    explanation:
      'DDoS attacks overwhelm networks/servers with massive amounts of traffic.',
    category: 'security',
    difficulty: 'easy',
  },
  {
    id: 'sec-008',
    question: 'What is port forwarding used for?',
    options: {
      a: 'Blocking specific ports',
      b: 'Directing external traffic to internal devices',
      c: 'Encrypting port data',
    },
    correctAnswer: 'b',
    explanation:
      'Port forwarding routes external requests to specific devices on your network.',
    category: 'security',
    difficulty: 'medium',
  },

  // ==================== SMART HOME QUESTIONS ====================
  {
    id: 'smart-001',
    question: 'What protocol do many smart home devices use for low-power communication?',
    options: {
      a: 'Bluetooth Classic',
      b: 'Zigbee',
      c: 'Ethernet',
    },
    correctAnswer: 'b',
    explanation:
      'Zigbee is a low-power, mesh protocol commonly used in smart home devices.',
    category: 'smart_home',
    difficulty: 'medium',
  },
  {
    id: 'smart-002',
    question: 'What is Matter in smart home technology?',
    options: {
      a: 'A specific brand of devices',
      b: 'A universal smart home standard',
      c: 'A type of smart switch',
    },
    correctAnswer: 'b',
    explanation:
      'Matter is a new universal standard for smart home interoperability.',
    category: 'smart_home',
    difficulty: 'medium',
  },
  {
    id: 'smart-003',
    question: 'What is a smart home "hub" used for?',
    options: {
      a: 'Boosting WiFi signal',
      b: 'Centralizing control of smart devices',
      c: 'Storing security footage',
    },
    correctAnswer: 'b',
    explanation:
      'A hub acts as a central controller connecting and managing various smart devices.',
    category: 'smart_home',
    difficulty: 'easy',
  },
  {
    id: 'smart-004',
    question: 'What is Thread in smart home networking?',
    options: {
      a: 'A wiring technique',
      b: 'A low-power mesh networking protocol',
      c: 'A voice assistant',
    },
    correctAnswer: 'b',
    explanation:
      'Thread is an IP-based, low-power mesh protocol designed for smart home devices.',
    category: 'smart_home',
    difficulty: 'hard',
  },

  // ==================== TROUBLESHOOTING QUESTIONS ====================
  {
    id: 'trouble-001',
    question: 'What is the first step when troubleshooting network issues?',
    options: {
      a: 'Replace all cables',
      b: 'Power cycle the equipment',
      c: 'Call the ISP',
    },
    correctAnswer: 'b',
    explanation:
      'Power cycling (restart) resolves many common network issues and is the first step.',
    category: 'troubleshooting',
    difficulty: 'easy',
  },
  {
    id: 'trouble-002',
    question: 'What does a blinking amber light usually indicate on network equipment?',
    options: {
      a: 'Normal operation',
      b: 'No connection or error',
      c: 'High speed connection',
    },
    correctAnswer: 'b',
    explanation:
      'Amber/orange LEDs typically indicate errors, warnings, or connectivity issues.',
    category: 'troubleshooting',
    difficulty: 'easy',
  },
  {
    id: 'trouble-003',
    question: 'What command tests connectivity to a remote host?',
    options: {
      a: 'ipconfig',
      b: 'ping',
      c: 'netstat',
    },
    correctAnswer: 'b',
    explanation:
      'The ping command sends ICMP packets to test if a host is reachable.',
    category: 'troubleshooting',
    difficulty: 'easy',
  },
  {
    id: 'trouble-004',
    question: 'What does "IP conflict" mean?',
    options: {
      a: 'No internet connection',
      b: 'Two devices have the same IP address',
      c: 'IP address is blocked',
    },
    correctAnswer: 'b',
    explanation:
      'IP conflict occurs when two devices on the same network have identical IP addresses.',
    category: 'troubleshooting',
    difficulty: 'easy',
  },
  {
    id: 'trouble-005',
    question: 'What should you check if only one device has no internet?',
    options: {
      a: 'ISP status',
      b: 'Device\'s network settings and connection',
      c: 'Replace the router',
    },
    correctAnswer: 'b',
    explanation:
      'If only one device is affected, the issue is likely device-specific, not network-wide.',
    category: 'troubleshooting',
    difficulty: 'easy',
  },
  {
    id: 'trouble-006',
    question: 'What does traceroute show?',
    options: {
      a: 'Current IP address',
      b: 'The path packets take to a destination',
      c: 'Open ports on a device',
    },
    correctAnswer: 'b',
    explanation:
      'Traceroute shows each hop (router) packets pass through to reach a destination.',
    category: 'troubleshooting',
    difficulty: 'medium',
  },
  {
    id: 'trouble-007',
    question: 'What causes "double NAT" issues?',
    options: {
      a: 'Two devices with the same MAC',
      b: 'Two routers performing NAT in sequence',
      c: 'Duplicate DNS servers',
    },
    correctAnswer: 'b',
    explanation:
      'Double NAT occurs when traffic passes through two NAT devices, causing port forwarding issues.',
    category: 'troubleshooting',
    difficulty: 'hard',
  },

  // ==================== GENERAL QUESTIONS ====================
  {
    id: 'gen-001',
    question: 'What does "bandwidth" measure?',
    options: {
      a: 'Network security level',
      b: 'Data transfer capacity',
      c: 'Physical cable length',
    },
    correctAnswer: 'b',
    explanation:
      'Bandwidth measures the maximum data transfer rate of a network connection.',
    category: 'general',
    difficulty: 'easy',
  },
  {
    id: 'gen-002',
    question: 'What is the difference between upload and download speed?',
    options: {
      a: 'There is no difference',
      b: 'Download is data coming in, upload is data going out',
      c: 'Upload is always faster',
    },
    correctAnswer: 'b',
    explanation:
      'Download speed is data received; upload speed is data sent from your device.',
    category: 'general',
    difficulty: 'easy',
  },
  {
    id: 'gen-003',
    question: 'What is latency in networking?',
    options: {
      a: 'Internet speed',
      b: 'Time delay for data to travel',
      c: 'Number of connected devices',
    },
    correctAnswer: 'b',
    explanation:
      'Latency (ping) is the time it takes for data to travel between two points.',
    category: 'general',
    difficulty: 'easy',
  },
  {
    id: 'gen-004',
    question: 'What is jitter in a network connection?',
    options: {
      a: 'Variation in latency over time',
      b: 'Total bandwidth available',
      c: 'Number of packet drops',
    },
    correctAnswer: 'a',
    explanation:
      'Jitter is inconsistent latency, causing issues with real-time applications like VoIP.',
    category: 'general',
    difficulty: 'medium',
  },
  {
    id: 'gen-005',
    question: 'What does Mbps stand for?',
    options: {
      a: 'Megabytes per second',
      b: 'Megabits per second',
      c: 'Million bytes per second',
    },
    correctAnswer: 'b',
    explanation:
      'Mbps is Megabits per second (8 megabits = 1 megabyte).',
    category: 'general',
    difficulty: 'easy',
  },
];

// ============================================================================
// QUIZ GENERATION
// ============================================================================

/**
 * Get the current week's quiz ID in ISO week format: YYYY-Www
 * Quiz resets Monday at 6am NSW Australia time.
 * Before Monday 6am NSW → previous week's ID is returned.
 */
export function getCurrentWeekId(): string {
  const now = new Date();
  const nswTime = toZonedTime(now, getTimezoneSync());

  // If before 6am on Monday, treat as previous week
  const dayOfWeek = nswTime.getDay(); // 0=Sun, 1=Mon
  const nswHour = nswTime.getHours();
  if (dayOfWeek === 1 && nswHour < QUIZ_RESET_HOUR) {
    // It's Monday before 6am — roll back to previous week
    const adjusted = new Date(nswTime);
    adjusted.setDate(adjusted.getDate() - 1);
    const weekNum = getISOWeek(adjusted);
    const weekYear = getISOWeekYear(adjusted);
    return `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
  }

  const weekNum = getISOWeek(nswTime);
  const weekYear = getISOWeekYear(nswTime);
  return `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
}

/** @deprecated Use getCurrentWeekId() instead */
export const getTodayDate = getCurrentWeekId;

/**
 * Calculate the number of days until the next quiz reset (Monday 6am NSW).
 * Returns 0 if a new quiz is already available.
 */
export function getNextQuizResetDate(): { daysUntil: number; resetDate: Date } {
  const now = new Date();
  const nswNow = toZonedTime(now, getTimezoneSync());
  const dayOfWeek = nswNow.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  // Days until next Monday
  let daysUntilMonday = (1 - dayOfWeek + 7) % 7;
  if (daysUntilMonday === 0) {
    // It's Monday — if before 6am, reset is today; otherwise next Monday
    if (nswNow.getHours() >= QUIZ_RESET_HOUR) {
      daysUntilMonday = 7;
    }
  }

  const resetDate = new Date(nswNow);
  resetDate.setDate(resetDate.getDate() + daysUntilMonday);
  resetDate.setHours(QUIZ_RESET_HOUR, 0, 0, 0);

  return { daysUntil: daysUntilMonday, resetDate };
}

/**
 * Calculate the number of weeks between two ISO week IDs (e.g. "2026-W14", "2026-W15")
 * Returns positive if weekB is after weekA.
 */
function getWeekDifference(weekA: string, weekB: string): number {
  // Parse YYYY-Www format
  const parseWeek = (w: string) => {
    const match = w.match(/^(\d{4})-W(\d{2})$/);
    if (!match) return startOfISOWeek(new Date());
    const year = parseInt(match[1], 10);
    const week = parseInt(match[2], 10);
    // Jan 4 is always in ISO week 1
    const jan4 = new Date(year, 0, 4);
    const start = startOfISOWeek(jan4);
    start.setDate(start.getDate() + (week - 1) * 7);
    return start;
  };

  const dateA = parseWeek(weekA);
  const dateB = parseWeek(weekB);
  const daysDiff = differenceInDays(dateB, dateA);
  return Math.round(daysDiff / 7);
}

// ============================================================================
// CURATED WEEKLY QUIZZES
// Add new weeks here. The key is the ISO week ID (YYYY-Www).
// If no curated quiz exists for the current week, falls back to random.
// ============================================================================

const WEEKLY_QUIZZES: Record<string, QuizQuestion[]> = {
  // ==================== WEEK OF April 27 - May 3, 2026 ====================
  '2026-W18': [
    {
      id: 'w18-001',
      question: 'What does DNS actually do?',
      options: {
        a: 'Translates website names like google.com into the IP addresses computers actually use',
        b: 'Encrypts your internet traffic so your ISP can\'t see what you\'re up to',
        c: 'Stores your browsing history on a server somewhere overseas',
      },
      correctAnswer: 'a',
      explanation:
        'DNS (Domain Name System) is the internet\'s phonebook. You type "google.com", DNS looks up the IP, your device connects there. Without it we\'d all be memorising numbers.',
      category: 'networking',
      difficulty: 'easy',
    },
    {
      id: 'w18-002',
      question: 'Why does turning a router off and on again actually fix so many things?',
      options: {
        a: 'The cooldown lets the components physically reset their temperatures',
        b: 'It clears stuck memory, kills broken connections, and grabs a fresh IP',
        c: 'Modern routers run a maintenance script every time they boot',
      },
      correctAnswer: 'b',
      explanation:
        'Power cycle = clear the RAM, kill stuck processes, drop broken connections, fresh DHCP lease from the ISP. Easily 90% of "no internet" calls — ours included — get solved by 30 seconds unplugged.',
      category: 'troubleshooting',
      difficulty: 'easy',
    },
    {
      id: 'w18-003',
      question: 'What does "WiFi" actually stand for?',
      options: {
        a: 'Wireless Fidelity',
        b: 'Wide Frequency',
        c: 'Nothing — it\'s just a catchy brand name',
      },
      correctAnswer: 'c',
      explanation:
        'Surprise! WiFi doesn\'t stand for anything. The WiFi Alliance picked it because it rhymed with HiFi. "Wireless Fidelity" is a backronym people made up later.',
      category: 'wifi',
      difficulty: 'easy',
    },
    {
      id: 'w18-004',
      question: 'What\'s the official nickname SpaceX uses for the Starlink dish?',
      options: {
        a: 'Dishy McFlatface',
        b: 'Star Pancake',
        c: 'The Disc',
      },
      correctAnswer: 'a',
      explanation:
        '"Dishy McFlatface" — straight from SpaceX, in the actual documentation. Yes really. Solid bit of small-talk to drop on a customer when the conversation dries up.',
      category: 'starlink',
      difficulty: 'easy',
    },
    {
      id: 'w18-005',
      question: 'What\'s a MAC address?',
      options: {
        a: 'The login password for an Apple Mac computer',
        b: 'A unique hardware ID burned into every network device at the factory',
        c: 'The internet address your router uses when you\'re online',
      },
      correctAnswer: 'b',
      explanation:
        'MAC = Media Access Control. Every network card has one — burned in at the factory, looks like AA:BB:CC:11:22:33. Nothing to do with Apple. It\'s how your switch knows which device is which on the local network.',
      category: 'networking',
      difficulty: 'easy',
    },
  ],

  // ==================== WEEK OF April 20-26, 2026 ====================
  '2026-W17': [
    {
      id: 'w17-001',
      question: 'A customer says "my internet is down". What\'s the FIRST thing to check before sending a tech?',
      options: {
        a: 'Whether other devices in the same house are also offline — if only one device is affected, it\'s a device issue, not a service outage',
        b: 'Whether the account is paid up to date — most "no internet" calls turn out to be a billing suspension rather than a fault',
        c: 'Whether the modem or router is still under warranty — a hardware failure is the most common cause so confirming warranty avoids a callout charge',
      },
      correctAnswer: 'a',
      explanation:
        'If only one device is affected, it\'s a device issue (WiFi password, settings, driver). If everything in the house is down, it\'s the connection. This one question saves a callout.',
      category: 'troubleshooting',
      difficulty: 'easy',
    },
    {
      id: 'w17-002',
      question: 'What\'s the main difference between a Standard Starlink kit and a Starlink Mini?',
      options: {
        a: 'Standard ships with a separate large dish plus a mains-powered router and hits higher peak speeds; Mini is an all-in-one portable unit with built-in WiFi that draws less power, better suited to sheds, caravans, and smaller sites',
        b: 'Standard uses the newer v2 satellite fleet while Mini is restricted to the older v1 satellites, so Mini sees noticeably higher latency and more frequent drop-outs in bad weather',
        c: 'Standard is licensed for residential addresses only while Mini is the commercial/business SKU — the hardware inside is the same, just branded and priced differently',
      },
      correctAnswer: 'a',
      explanation:
        'The Mini is a portable all-in-one (dish + WiFi in one device), lower power draw, perfect for caravans, sheds, and small sites. The Standard kit has a separate dish + router with higher peak throughput, designed for permanent home installs.',
      category: 'starlink',
      difficulty: 'easy',
    },
    {
      id: 'w17-003',
      question: 'In our deal pipeline, what happens at the "Pre-Site" stage?',
      options: {
        a: 'The customer pays a 30% deposit to lock in the install date and confirm they\'re committed — the tech doesn\'t visit until after the deposit has cleared',
        b: 'A tech visits the site to scope the job — walking each zone, capturing equipment needs and access details, taking photos, and submitting a scope before a quote is built',
        c: 'Office signs off the final invoice and closes the deal — all on-site work and provisioning has already wrapped, Pre-Site is just the billing confirmation step',
      },
      correctAnswer: 'b',
      explanation:
        'Pre-Site is the on-site scoping visit — the tech walks the property, captures equipment needs per zone, takes photos, and submits a scope. That feeds into an accurate quote before the install is booked in.',
      category: 'company',
      difficulty: 'easy',
    },
    {
      id: 'w17-004',
      question: 'A customer\'s WiFi reaches the lounge fine but drops out in the back shed 30m away. What\'s the most reliable fix?',
      options: {
        a: 'Move the main router closer to the shed — repositioning the existing hardware into the middle of the property usually restores coverage to both ends without adding new gear',
        b: 'Run an ethernet cable to a new access point mounted in or near the shed — a wired AP gives full-speed coverage in the dead zone with no signal loss',
        c: 'Swap the password to a longer WPA3 key and reboot the router — a stronger security profile clears the authentication handshake issues that cause distant drop-outs',
      },
      correctAnswer: 'b',
      explanation:
        'A wired access point in the dead zone is the gold-standard fix — full-speed coverage with no signal loss. Wireless mesh works in a pinch, but ethernet-backed APs always outperform it for range and reliability.',
      category: 'wifi',
      difficulty: 'easy',
    },
    {
      id: 'w17-005',
      question: 'What does "PoE" mean for a UniFi access point install?',
      options: {
        a: 'One ethernet cable from the switch carries both data AND power to the AP — no separate power outlet needed at the mounting location',
        b: 'The AP still needs its own 240V power point at every install location, and the ethernet cable handles data only (so an electrician is needed alongside the tech)',
        c: 'PoE is a cabling standard that applies only to cameras and intercoms — UniFi access points use a different proprietary power injector that must be bought separately',
      },
      correctAnswer: 'a',
      explanation:
        'Power-over-Ethernet means a single Cat6 run from the switch delivers data + power. That\'s why we can mount APs in ceilings, eaves, and sheds without needing a sparkie to add a power point at every location.',
      category: 'networking',
      difficulty: 'easy',
    },
  ],

  // ==================== LAUNCH WEEK (April 6-12, 2026) ====================
  '2026-W15': [
    {
      id: 'launch-001',
      question: 'What does THEO stand for?',
      options: {
        a: 'Technical Hardware Engineering Operations',
        b: 'Techery\'s Home of Efficient Operations',
        c: 'Techery Help and Equipment Ordering',
      },
      correctAnswer: 'b',
      explanation:
        'THEO — Techery\'s Home of Efficient Operations — is the platform that runs everything from sales through to job completion.',
      category: 'company',
      difficulty: 'easy',
    },
    {
      id: 'launch-002',
      question: 'What is the name of Amazon\'s satellite internet project that competes with Starlink?',
      options: {
        a: 'Project Orbit',
        b: 'Project Kuiper',
        c: 'Project Skybridge',
      },
      correctAnswer: 'b',
      explanation:
        'Project Kuiper is Amazon\'s low-Earth orbit satellite constellation aiming to provide broadband internet — a direct competitor to SpaceX\'s Starlink.',
      category: 'starlink',
      difficulty: 'easy',
    },
    // NOTE: 'launch-003' (office phone trivia: '(02) 4883 6781') was removed
    // May 2026 — it hardcoded Techery's office number. The broader launch-*
    // quiz set is still Techery-specific employee onboarding content and
    // should migrate to per-tenant configuration alongside service plan
    // seeds (Phase 4 SaaS-readiness work). Question id intentionally not
    // reused so existing quiz_attempts referencing 'launch-003' (if any)
    // don't reattach to a different question.
    {
      id: 'launch-004',
      question: 'Crestron has been a leader in automation and control systems for roughly how many years?',
      options: {
        a: 'About 35 years',
        b: 'About 55 years',
        c: 'About 75 years',
      },
      correctAnswer: 'b',
      explanation:
        'Crestron was founded in 1971 — that\'s over 50 years of building commercial and residential automation systems.',
      category: 'general',
      difficulty: 'easy',
    },
    {
      id: 'launch-005',
      question: 'What is PoE and why do we use it for UniFi access points?',
      options: {
        a: 'Power over Ethernet — delivers power and data over one cable',
        b: 'Protocol over Ethernet — a special networking protocol',
        c: 'Port over Ethernet — connects ports remotely',
      },
      correctAnswer: 'a',
      explanation:
        'Power over Ethernet lets us run a single cable to each access point for both data and power — no separate power outlet needed at each AP location.',
      category: 'networking',
      difficulty: 'easy',
    },
  ],
};

/**
 * Generate a weekly quiz — uses curated questions if available, otherwise random from bank
 */
export function generateWeeklyQuiz(
  weekId: string,
  questionCount: number = 5
): WeeklyQuiz {
  // Check for curated questions first
  const curated = WEEKLY_QUIZZES[weekId];
  if (curated && curated.length > 0) {
    return {
      weekId,
      questions: curated.slice(0, questionCount),
      generatedAt: new Date().toISOString(),
      generatedBy: 'curated',
      active: true,
    };
  }

  // Fallback: random from question bank (Fisher-Yates shuffle)
  const shuffled = [...QUESTION_BANK];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const categories: QuizCategory[] = [
    'unifi',
    'networking',
    'wifi',
    'starlink',
    'security',
    'smart_home',
    'troubleshooting',
    'general',
  ];

  const selectedQuestions: QuizQuestion[] = [];
  const usedIds = new Set<string>();

  // First pass: try to get one from each category
  for (const category of categories) {
    if (selectedQuestions.length >= questionCount) break;

    const categoryQuestion = shuffled.find(
      (q) => q.category === category && !usedIds.has(q.id)
    );

    if (categoryQuestion) {
      selectedQuestions.push(categoryQuestion);
      usedIds.add(categoryQuestion.id);
    }
  }

  // Fill remaining slots with random questions
  for (const question of shuffled) {
    if (selectedQuestions.length >= questionCount) break;
    if (!usedIds.has(question.id)) {
      selectedQuestions.push(question);
      usedIds.add(question.id);
    }
  }

  return {
    weekId,
    questions: selectedQuestions,
    generatedAt: new Date().toISOString(),
    generatedBy: 'auto',
    active: true,
  };
}

/** @deprecated Use generateWeeklyQuiz instead */
export const generateDailyQuiz = (date: string, questionCount?: number) =>
  generateWeeklyQuiz(date, questionCount);

// ============================================================================
// FIRESTORE OPERATIONS
// ============================================================================

/**
 * Get or create this week's quiz
 */
export async function getThisWeeksQuiz(): Promise<WeeklyQuiz> {
  const weekId = getCurrentWeekId();
  const quizRef = doc(db, 'weekly_quizzes', weekId);
  const quizSnap = await getDoc(quizRef);

  if (quizSnap.exists()) {
    return { id: quizSnap.id, ...quizSnap.data() } as WeeklyQuiz;
  }

  // Generate new quiz for this week. Stamp tenantId so the doc is
  // isolation-ready (single-doc gets are governed per-doc by tenantId).
  const newQuiz = generateWeeklyQuiz(weekId);
  await setDoc(quizRef, { ...newQuiz, tenantId: getTenantIdSync() });

  return { id: weekId, ...newQuiz };
}

/** @deprecated Use getThisWeeksQuiz instead */
export const getTodaysQuiz = getThisWeeksQuiz;

/**
 * Force-regenerate the current week's quiz using the latest curated questions.
 *
 * Deletes the cached Firestore doc AND every quiz_attempts doc for the current
 * week, then rebuilds stats for each affected tech. Without clearing attempts
 * the `hasCompletedThisWeek` gate in useWeeklyQuiz blocks techs who already did
 * the old (fallback random) version from ever seeing the new curated questions
 * until next Monday 6am — which defeats the whole point of regenerating.
 *
 * Returns the new quiz plus a summary of what was wiped so the admin UI can
 * surface it.
 */
export async function forceRegenerateThisWeeksQuiz(): Promise<{
  quiz: WeeklyQuiz;
  deletedAttempts: number;
  rebuiltTechStats: number;
}> {
  const weekId = getCurrentWeekId();
  const quizRef = doc(db, 'weekly_quizzes', weekId);

  // 1. Drop the cached weekly quiz doc (if any) so curated questions take effect
  const existing = await getDoc(quizRef);
  if (existing.exists()) {
    await deleteDoc(quizRef);
  }

  // 2. Delete every quiz_attempts doc for this week so techs aren't locked out
  //    by the `hasCompletedThisWeek` gate. Collect affected techIds for stats rebuild.
  const attemptsSnap = await getDocs(
    query(
      collection(db, 'quiz_attempts'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('quizDate', '==', weekId)
    )
  );
  const affectedTechIds = new Set<string>();
  for (const attemptDoc of attemptsSnap.docs) {
    const techId = (attemptDoc.data() as QuizAttempt).techId;
    if (techId) affectedTechIds.add(techId);
    await deleteDoc(attemptDoc.ref);
  }

  // 3. Rebuild stats for each affected tech from their remaining attempts
  for (const techId of affectedTechIds) {
    await rebuildTechQuizStats(techId);
  }

  // 4. Write the fresh quiz doc (stamp tenantId — see getThisWeeksQuiz).
  const newQuiz = generateWeeklyQuiz(weekId);
  await setDoc(quizRef, { ...newQuiz, tenantId: getTenantIdSync() });

  return {
    quiz: { id: weekId, ...newQuiz },
    deletedAttempts: attemptsSnap.size,
    rebuiltTechStats: affectedTechIds.size,
  };
}

/**
 * Get a specific quiz by week ID
 */
export async function getQuizByWeek(weekId: string): Promise<WeeklyQuiz | null> {
  const quizRef = doc(db, 'weekly_quizzes', weekId);
  const quizSnap = await getDoc(quizRef);

  if (quizSnap.exists()) {
    return { id: quizSnap.id, ...quizSnap.data() } as WeeklyQuiz;
  }

  return null;
}

/** @deprecated Use getQuizByWeek instead */
export const getQuizByDate = getQuizByWeek;

/**
 * Check if tech has completed this week's quiz
 */
export async function hasTechCompletedThisWeeksQuiz(
  techId: string
): Promise<boolean> {
  const weekId = getCurrentWeekId();

  const q = query(
    collection(db, 'quiz_attempts'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('techId', '==', techId),
    where('quizDate', '==', weekId),
    where('status', '==', 'completed')
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Get tech's attempt for this week's quiz
 */
export async function getTechThisWeeksAttempt(
  techId: string
): Promise<QuizAttempt | null> {
  const weekId = getCurrentWeekId();

  const q = query(
    collection(db, 'quiz_attempts'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('techId', '==', techId),
    where('quizDate', '==', weekId),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as QuizAttempt;
}

/**
 * Start a new quiz attempt
 */
export async function startQuizAttempt(
  techId: string,
  techName: string,
  quiz: WeeklyQuiz
): Promise<QuizAttempt> {
  const attempt: Omit<QuizAttempt, 'id'> = {
    quizId: quiz.weekId,
    quizDate: quiz.weekId,
    techId,
    techName,
    // Caller's tenant — REQUIRED by the rules lock. weekly_quizzes is GLOBAL
    // content (shared week id), so its own tenantId is whoever created the
    // week and must never be copied onto a tenant-isolated attempt.
    tenantId: getTenantIdSync(),
    answers: [],
    totalQuestions: quiz.questions.length,
    correctAnswers: 0,
    score: 0,
    startedAt: new Date().toISOString(),
    status: 'in_progress',
    currentQuestionIndex: 0,
    currentScreen: 'question',
  } as Omit<QuizAttempt, 'id'>;

  const docRef = await addDoc(collection(db, 'quiz_attempts'), attempt);
  return { id: docRef.id, ...attempt };
}

/**
 * Submit an answer for a quiz question
 */
export async function submitQuizAnswer(
  attemptId: string,
  questionId: string,
  selectedAnswer: 'a' | 'b' | 'c',
  isCorrect: boolean,
  currentAnswers: QuizAttempt['answers'],
  currentQuestionIndex: number
): Promise<void> {
  const newAnswer = {
    questionId,
    selectedAnswer,
    isCorrect,
    answeredAt: new Date().toISOString(),
  };

  const updatedAnswers = [...currentAnswers, newAnswer];

  await updateDoc(doc(db, 'quiz_attempts', attemptId), {
    answers: updatedAnswers,
    currentQuestionIndex,
    currentScreen: 'feedback', // After answering, show feedback
  });
}

/**
 * Save UI state when user navigates to next question or screen
 */
export async function saveQuizUIState(
  attemptId: string,
  currentQuestionIndex: number,
  currentScreen: 'intro' | 'question' | 'feedback' | 'results'
): Promise<void> {
  await updateDoc(doc(db, 'quiz_attempts', attemptId), {
    currentQuestionIndex,
    currentScreen,
  });
}

/**
 * Complete a quiz attempt and update stats
 */
export async function completeQuizAttempt(
  attemptId: string,
  answers: QuizAttempt['answers'],
  totalQuestions: number,
  quiz: WeeklyQuiz,
  techId: string,
  techName: string
): Promise<QuizAttempt> {
  // Ensure we don't count more answers than questions (data integrity check)
  const validAnswers = answers.slice(0, totalQuestions);
  const correctAnswers = validAnswers.filter((a) => a.isCorrect).length;

  // Cap score at 100% to prevent bug where score > 100%
  const rawScore = (correctAnswers / totalQuestions) * 100;
  const score = Math.min(100, Math.round(rawScore));
  const startedAt = new Date().toISOString(); // Will be replaced with actual

  // Get the actual attempt to get startedAt
  const attemptRef = doc(db, 'quiz_attempts', attemptId);
  const attemptSnap = await getDoc(attemptRef);
  const attemptData = attemptSnap.data();

  const completedAt = new Date().toISOString();
  const timeSpentSeconds = attemptData?.startedAt
    ? Math.round(
        (new Date(completedAt).getTime() -
          new Date(attemptData.startedAt).getTime()) /
          1000
      )
    : 0;

  const updates = {
    answers: validAnswers, // Use only valid answers
    correctAnswers,
    score,
    completedAt,
    timeSpentSeconds,
    status: 'completed' as const,
    currentQuestionIndex: totalQuestions - 1, // Last question
    currentScreen: 'results' as const, // Show results screen
  };

  await updateDoc(attemptRef, updates);

  // Update tech's stats (don't fail the attempt if stats update fails)
  try {
    await updateTechQuizStats(techId, techName, quiz, score, correctAnswers);
  } catch (statsError) {
    console.error('Failed to update quiz stats for leaderboard:', statsError);
    // Continue - the attempt is still completed, just stats weren't updated
  }

  return {
    id: attemptId,
    quizId: quiz.weekId,
    quizDate: quiz.weekId,
    techId,
    techName,
    totalQuestions,
    startedAt: attemptData?.startedAt || startedAt,
    ...updates,
  };
}

/**
 * Update tech's quiz stats after completing a quiz
 */
async function updateTechQuizStats(
  techId: string,
  techName: string,
  quiz: WeeklyQuiz,
  score: number,
  correctAnswers: number
): Promise<void> {
  // Validate techId to prevent creating invalid documents
  if (!techId || techId.trim() === '') {
    console.error('Cannot update quiz stats: techId is empty or invalid');
    return;
  }

  const statsRef = doc(db, 'quiz_stats', techId);
  const statsSnap = await getDoc(statsRef);
  const currentWeek = getCurrentWeekId();

  if (statsSnap.exists()) {
    const stats = statsSnap.data() as TechQuizStats;

    // Calculate streak — consecutive weeks
    let currentStreak = stats.currentStreak || 0;
    const lastQuizWeek = stats.lastQuizDate;

    if (lastQuizWeek) {
      const weeksDiff = getWeekDifference(lastQuizWeek, currentWeek);
      if (weeksDiff === 1) {
        currentStreak += 1;
      } else if (weeksDiff > 1) {
        currentStreak = 1;
      }
      // If same week, keep streak
    } else {
      currentStreak = 1;
    }

    const longestStreak = Math.max(stats.longestStreak || 0, currentStreak);

    // Update category stats
    const categoryStats = { ...stats.categoryStats };
    for (const question of quiz.questions) {
      if (!categoryStats[question.category]) {
        categoryStats[question.category] = {
          totalAnswered: 0,
          correctAnswers: 0,
          averageScore: 0,
        };
      }
      categoryStats[question.category]!.totalAnswered += 1;
    }

    // Update recent scores (keep last 30)
    const recentScores = [
      {
        date: currentWeek,
        score,
        correctAnswers,
        totalQuestions: quiz.questions.length,
      },
      ...(stats.recentScores || []).slice(0, 29),
    ];

    // Calculate new lifetime average
    const totalQuizzes = stats.totalQuizzesTaken + 1;
    const totalCorrect = stats.totalCorrectAnswers + correctAnswers;
    const totalAnswered = stats.totalQuestionsAnswered + quiz.questions.length;
    const lifetimeAverageScore = Math.round((totalCorrect / totalAnswered) * 100);

    await updateDoc(statsRef, {
      // Stamp canonical tenantId so legacy docs become isolation-ready.
      tenantId: getTenantIdSync(),
      totalQuizzesTaken: totalQuizzes,
      totalQuestionsAnswered: totalAnswered,
      totalCorrectAnswers: totalCorrect,
      lifetimeAverageScore,
      currentStreak,
      longestStreak,
      lastQuizDate: currentWeek,
      categoryStats,
      recentScores,
      lastUpdated: new Date().toISOString(),
    });
  } else {
    // Create new stats document
    const newStats: TechQuizStats = {
      techId,
      techName,
      tenantId: getTenantIdSync(),
      totalQuizzesTaken: 1,
      totalQuestionsAnswered: quiz.questions.length,
      totalCorrectAnswers: correctAnswers,
      lifetimeAverageScore: score,
      currentStreak: 1,
      longestStreak: 1,
      lastQuizDate: currentWeek,
      categoryStats: {},
      recentScores: [
        {
          date: currentWeek,
          score,
          correctAnswers,
          totalQuestions: quiz.questions.length,
        },
      ],
      firstQuizAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
    };

    // Initialize category stats
    for (const question of quiz.questions) {
      if (!newStats.categoryStats[question.category]) {
        newStats.categoryStats[question.category] = {
          totalAnswered: 0,
          correctAnswers: 0,
          averageScore: 0,
        };
      }
      newStats.categoryStats[question.category]!.totalAnswered += 1;
    }

    await setDoc(statsRef, newStats);
  }
}

/**
 * Get a tech's quiz stats
 */
export async function getTechQuizStats(
  techId: string
): Promise<TechQuizStats | null> {
  const statsRef = doc(db, 'quiz_stats', techId);
  const statsSnap = await getDoc(statsRef);

  if (statsSnap.exists()) {
    return { id: statsSnap.id, ...statsSnap.data() } as TechQuizStats;
  }

  return null;
}

/**
 * Get all tech quiz stats (for leaderboard)
 */
export async function getAllTechQuizStats(): Promise<TechQuizStats[]> {
  const q = query(
    collection(db, 'quiz_stats'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    orderBy('lifetimeAverageScore', 'desc'),
    limit(20)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as TechQuizStats)
  );
}

/**
 * Get recent quiz attempts for all techs (for office view)
 */
export async function getRecentQuizAttempts(
  limitCount: number = 10
): Promise<QuizAttempt[]> {
  const q = query(
    collection(db, 'quiz_attempts'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as QuizAttempt)
  );
}

// ============================================================================
// ADMIN: ATTEMPT MANAGEMENT
// ============================================================================

/**
 * Get all quiz attempts (admin view).
 * Sorted newest-first by completedAt (falls back to startedAt for in-progress).
 */
export async function getAllQuizAttempts(): Promise<QuizAttempt[]> {
  // Tenant-filtered (no orderBy → single-field tenantId index suffices).
  const snapshot = await getDocs(
    query(
      collection(db, 'quiz_attempts'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync()))
    )
  );
  const attempts = snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as QuizAttempt)
  );
  return attempts.sort((a, b) => {
    const aTime = a.completedAt || a.startedAt || '';
    const bTime = b.completedAt || b.startedAt || '';
    return bTime.localeCompare(aTime);
  });
}

/**
 * Delete a single quiz attempt and rebuild that tech's stats from remaining attempts.
 */
export async function deleteQuizAttempt(attemptId: string): Promise<void> {
  // Get the attempt first so we know which tech to rebuild stats for
  const attemptRef = doc(db, 'quiz_attempts', attemptId);
  const attemptSnap = await getDoc(attemptRef);
  if (!attemptSnap.exists()) return;

  const techId = (attemptSnap.data() as QuizAttempt).techId;

  await deleteDoc(attemptRef);

  if (techId) {
    await rebuildTechQuizStats(techId);
  }
}

/**
 * Rebuild a tech's quiz_stats document from their remaining completed attempts.
 * Deletes the stats doc entirely if no completed attempts remain.
 */
export async function rebuildTechQuizStats(techId: string): Promise<void> {
  if (!techId) return;

  const q = query(
    collection(db, 'quiz_attempts'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('techId', '==', techId),
    where('status', '==', 'completed')
  );
  const snapshot = await getDocs(q);

  const statsRef = doc(db, 'quiz_stats', techId);

  if (snapshot.empty) {
    // No attempts left — clear the stats doc entirely
    const existing = await getDoc(statsRef);
    if (existing.exists()) {
      await deleteDoc(statsRef);
    }
    return;
  }

  // Sort attempts oldest-first so streak calculation is chronological
  const attempts = snapshot.docs
    .map((d) => d.data() as QuizAttempt)
    .sort((a, b) => (a.quizDate || '').localeCompare(b.quizDate || ''));

  const techName = attempts[attempts.length - 1].techName;
  const totalQuizzesTaken = attempts.length;
  const totalQuestionsAnswered = attempts.reduce(
    (sum, a) => sum + (a.totalQuestions || 0),
    0
  );
  const totalCorrectAnswers = attempts.reduce(
    (sum, a) => sum + (a.correctAnswers || 0),
    0
  );
  const lifetimeAverageScore = totalQuestionsAnswered
    ? Math.round((totalCorrectAnswers / totalQuestionsAnswered) * 100)
    : 0;

  // Streak: walk through quizDate values, count consecutive weeks ending at the most recent
  const uniqueWeeks = Array.from(new Set(attempts.map((a) => a.quizDate))).sort();
  let currentStreak = 1;
  let longestStreak = 1;
  let runningStreak = 1;
  for (let i = 1; i < uniqueWeeks.length; i++) {
    const diff = getWeekDifference(uniqueWeeks[i - 1], uniqueWeeks[i]);
    if (diff === 1) {
      runningStreak += 1;
    } else {
      runningStreak = 1;
    }
    longestStreak = Math.max(longestStreak, runningStreak);
  }
  currentStreak = runningStreak;

  // Recent scores (last 30 weeks)
  const recentScores = attempts
    .slice(-30)
    .reverse()
    .map((a) => ({
      date: a.quizDate,
      score: a.score || 0,
      correctAnswers: a.correctAnswers || 0,
      totalQuestions: a.totalQuestions || 0,
    }));

  const firstQuizAt = attempts[0].startedAt;
  const lastQuizDate = uniqueWeeks[uniqueWeeks.length - 1];

  const rebuilt: TechQuizStats = {
    techId,
    techName,
    tenantId: getTenantIdSync(),
    totalQuizzesTaken,
    totalQuestionsAnswered,
    totalCorrectAnswers,
    lifetimeAverageScore,
    currentStreak,
    longestStreak,
    lastQuizDate,
    categoryStats: {},
    recentScores,
    firstQuizAt,
    lastUpdated: new Date().toISOString(),
  };

  await setDoc(statsRef, rebuilt);
}

/**
 * Get quiz completion stats for a date range (for weekly/monthly views)
 */
export async function getQuizStatsForRange(
  startDate: string,
  endDate: string
): Promise<{
  totalAttempts: number;
  averageScore: number;
  participationRate: number;
  topPerformers: { techName: string; averageScore: number }[];
}> {
  const q = query(
    collection(db, 'quiz_attempts'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('status', '==', 'completed'),
    where('quizDate', '>=', startDate),
    where('quizDate', '<=', endDate)
  );

  const snapshot = await getDocs(q);
  const attempts = snapshot.docs.map((doc) => doc.data() as QuizAttempt);

  if (attempts.length === 0) {
    return {
      totalAttempts: 0,
      averageScore: 0,
      participationRate: 0,
      topPerformers: [],
    };
  }

  const totalScore = attempts.reduce((sum, a) => sum + a.score, 0);
  const averageScore = Math.round(totalScore / attempts.length);

  // Group by tech for top performers
  const techScores: { [techId: string]: { name: string; scores: number[] } } =
    {};
  for (const attempt of attempts) {
    if (!techScores[attempt.techId]) {
      techScores[attempt.techId] = { name: attempt.techName, scores: [] };
    }
    techScores[attempt.techId].scores.push(attempt.score);
  }

  const topPerformers = Object.values(techScores)
    .map((t) => ({
      techName: t.name,
      averageScore: Math.round(
        t.scores.reduce((a, b) => a + b, 0) / t.scores.length
      ),
    }))
    .sort((a, b) => b.averageScore - a.averageScore)
    .slice(0, 5);

  return {
    totalAttempts: attempts.length,
    averageScore,
    participationRate: 0, // Would need total techs to calculate
    topPerformers,
  };
}
